import { useCallback, useState } from "react"
import { Form, FormFeedback, Label, Button, Row, Col, Badge, Input } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  compressPdfFile,
  formatFileSize,
  MAX_PDF_SIZE_BYTES,
} from "components/Common/PdfAttachmentUpload"
import {
  useCreateRtgsBankTemplate,
  useEditRtgsBankTemplate,
} from "hooks/useRtgsBankTemplate"

import { COORD_FIELDS, DEFAULT_COORDS } from "./rtgsTemplateFields"

const validationSchema = Yup.object({
  bank_name: Yup.string().trim().required("Bank name is required"),
  is_active: Yup.boolean(),
})

const isPdfFile = file => {
  const type = String(file?.type || "").toLowerCase()
  const name = String(file?.name || "").toLowerCase()

  return type === "application/pdf" || name.endsWith(".pdf")
}

const fileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "")
    reader.onerror = () => reject(new Error("Could not read the template file."))
    reader.readAsDataURL(file)
  })

const RtgsBankTemplateAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateRtgsBankTemplate()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditRtgsBankTemplate()

  const [templateFile, setTemplateFile] = useState(null)
  const [processing, setProcessing] = useState(false)

  const resetFile = useCallback(() => {
    setTemplateFile(null)
  }, [])

  const buildTemplateFilePayload = useCallback(async () => {
    if (!templateFile) {
      return null
    }

    const { uploadFile } = await compressPdfFile({ file: templateFile })
    const base64 = await fileToBase64(uploadFile)

    if (!base64) {
      throw new Error("Template file could not be processed.")
    }

    return {
      template_file: base64,
      template_file_name: uploadFile.name || templateFile.name || "rtgs-template.pdf",
    }
  }, [templateFile])

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      setProcessing(true)

      const payload = {
        bank_name: values.bank_name.trim(),
        is_active: values.is_active ? 1 : 0,
      }

      if (!isEdit) {
        payload.overlay_config = {}
        COORD_FIELDS.forEach(({ key }) => {
          const c = DEFAULT_COORDS[key]
          payload.overlay_config[key] = {
            left: c.left,
            top: c.top,
            font_size: c.font_size,
            letter_spacing: c.letter_spacing,
            bold: c.bold ? 1 : 0,
          }
        })
      }

      try {
        const templatePayload = await buildTemplateFilePayload()
        if (templatePayload) {
          Object.assign(payload, templatePayload)
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, data: payload })
          : createMutation(payload)

        const res = await action
        toast.success(
          res?.message ??
            `Bank template ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        resetFile()
        toggleModal()
      } catch (error) {
        if (error?.message) {
          toast.error(error.message)
        }
      } finally {
        setProcessing(false)
      }
    },
    [
      isEdit,
      editedData,
      createMutation,
      editMutation,
      toggleModal,
      buildTemplateFilePayload,
      resetFile,
    ]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      bank_name: editedData?.bank_name ?? "",
      is_active: editedData?.is_active ?? true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const isLoading = createLoading || editLoading || processing
  const currentTemplateName = editedData?.template_file_name

  const handleClose = () => {
    resetFile()
    toggleModal()
  }

  const handleTemplateFileChange = event => {
    const file = event.target.files?.[0]
    event.target.value = ""

    if (!file) {
      return
    }

    if (!isPdfFile(file)) {
      toast.error("Only PDF template files are allowed.")
      return
    }

    setTemplateFile(file)
  }

  return (
    <ThemeModal
      isOpen={modal}
      toggle={handleClose}
      title={isEdit ? "Edit Bank Template" : "Add Bank Template"}
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <Button
            type="button"
            color="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="success"
            form="rtgs-bank-template-form"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : isEdit ? "Save Changes" : "Add Template"}
          </Button>
        </div>
      }
    >
      <Form id="rtgs-bank-template-form" onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={8}>
            <div className="mb-3">
              <Label htmlFor="bank_name">
                Bank Name <span className="text-danger">*</span>
              </Label>
              <InputField
                name="bank_name"
                id="bank_name"
                value={formik.values.bank_name}
                onChange={formik.handleChange}
                invalid={formik.touched.bank_name && !!formik.errors.bank_name}
                placeholder="e.g. Pubali Bank PLC"
              />
              {formik.touched.bank_name && formik.errors.bank_name && (
                <FormFeedback type="invalid">
                  {formik.errors.bank_name}
                </FormFeedback>
              )}
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center pt-1">
            <Button
              type="button"
              color={formik.values.is_active ? "success" : "secondary"}
              outline={!formik.values.is_active}
              size="sm"
              onClick={() =>
                formik.setFieldValue("is_active", !formik.values.is_active)
              }
            >
              <i
                className={`bx ${
                  formik.values.is_active ? "bx-check-circle" : "bx-x-circle"
                } me-1`}
              />
              {formik.values.is_active ? "Active" : "Inactive"}
            </Button>
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="template_file">Template File</Label>
          {editedData?.has_template_file && (
            <div className="mb-1">
              <Badge color="info" className="me-2">
                Current file uploaded
              </Badge>
              <small className="text-muted">
                {currentTemplateName
                  ? `${currentTemplateName}. Upload a new PDF to replace it.`
                  : "Upload a new PDF to replace it."}
              </small>
            </div>
          )}
          {templateFile && (
            <div className="border rounded bg-light px-2 py-1 mb-2 d-flex align-items-center justify-content-between gap-2">
              <span className="text-truncate">
                <i className="mdi mdi-file-pdf-box text-danger me-1" />
                {templateFile.name}
              </span>
              <span className="text-muted small flex-shrink-0">
                {formatFileSize(templateFile.size)}
              </span>
              <Button
                type="button"
                color="link"
                className="text-danger p-0 flex-shrink-0"
                onClick={resetFile}
              >
                <i className="mdi mdi-close" />
              </Button>
            </div>
          )}
          <Input
            type="file"
            id="template_file"
            accept="application/pdf,.pdf"
            onChange={handleTemplateFileChange}
            disabled={isLoading}
          />
          <small className="text-muted">
            Upload a PDF. It is compressed before upload and the final file must
            be {formatFileSize(MAX_PDF_SIZE_BYTES)} or smaller. The file is stored
            in the database so it survives server updates. Leave empty to keep the
            current file.
          </small>
        </div>

        <div className="alert alert-info mb-0 py-2">
          <i className="mdi mdi-information-outline me-1" />
          After saving and uploading a pad template, use the{" "}
          <strong>
            <i className="mdi mdi-target" /> Configure
          </strong>{" "}
          button on the list to place fields visually on the pad.
        </div>
      </Form>
    </ThemeModal>
  )
}

export default RtgsBankTemplateAddEdit
